import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { fetchS3UrlBothQuality } from '../file/withS3Url'

interface Props {
    src: string
    annotation: Nullable<string>
    title: string
    fullSizeSrc: string
    deps: CommonDeps
    imageClass?: string
    clickableArea?: 'top-left'
    tile?: boolean
}

const limitNumber = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const FULLSCREEN_QUERY_KEY = 'FULLSCREEN_IMG'

export const InspectableImage = ({
    src,
    annotation,
    title,
    imageClass,
    clickableArea,
    tile,
    fullSizeSrc,
    deps,
}: Props) => {
    const contentViewBox = useRef<HTMLDivElement>()
    const contentWrapper = useRef<HTMLDivElement>()
    const image = useRef<HTMLDivElement>()
    const [fullScreenSrc, setFullScreenSrc] = useState<string>(src)

    const [isInspecting, setIsInspecting] = useState(false)
    const [isInitialised, setIsInitialised] = useState(false)
    const [positionData, setPositionData] = useState({
        zoom: 100,
        left: 0,
        top: 0,
        offsetX: 0,
        offsetY: 0,
        maxX: 0,
        maxY: 0,
    })

    const getPosition = (zoomRatioChange: number = 1) => {
        if (contentWrapper.current == null || contentViewBox.current == null) return { top: 0, left: 0 }
        const wrapperWidth = contentWrapper.current.clientWidth
        const wrapperHeight = contentWrapper.current.clientHeight
        const innerWidth = contentViewBox.current.clientWidth * zoomRatioChange
        const innerHeight = contentViewBox.current.clientHeight * zoomRatioChange

        const left = Math.round((wrapperWidth - innerWidth) / 2)
        const top = Math.round((wrapperHeight - innerHeight) / 2)

        return {
            top,
            left,
        }
    }

    const getOffsetLimits = (zoomRatioChange: number) => {
        if (contentWrapper.current == null || contentViewBox.current == null || image.current == null) {
            return { maxX: 0, maxY: 0 }
        }
        const imageHeight = image.current.clientHeight
        const imageWidth = image.current.clientWidth
        const widthRatio = imageWidth / imageHeight
        const heightRatio = imageHeight / imageWidth
        const containerWidth = contentWrapper.current.clientWidth
        const containerHeight = contentWrapper.current.clientHeight

        const containerWidthRatio = containerWidth / containerHeight
        const containerHeightRatio = containerHeight / containerWidth

        const effectiveImageWidth =
            widthRatio < containerWidthRatio
                ? contentViewBox.current.clientHeight * zoomRatioChange * widthRatio
                : contentViewBox.current.clientWidth * zoomRatioChange
        const wrapperWidth = contentWrapper.current.clientWidth
        const maxX = Math.max(0, Math.round((effectiveImageWidth - wrapperWidth) / 2))

        const effectiveImageHeight =
            heightRatio < containerHeightRatio
                ? contentViewBox.current.clientWidth * zoomRatioChange * heightRatio
                : contentViewBox.current.clientHeight * zoomRatioChange
        const wrapperHeight = contentWrapper.current.clientHeight
        const maxY = Math.max(0, Math.round((effectiveImageHeight - wrapperHeight) / 2))

        return {
            maxX,
            maxY,
        }
    }

    useEffect(() => {
        const onWindowResize = () => {
            setPositionData(({ zoom }) => {
                const position = getPosition()
                const { maxX, maxY } = getOffsetLimits(1)
                return {
                    ...position,
                    zoom,
                    offsetX: 0,
                    offsetY: 0,
                    maxX,
                    maxY,
                }
            })
        }

        const onHashchange = ({ oldURL }: { oldURL: string }) => {
            if (oldURL.includes(FULLSCREEN_QUERY_KEY)) {
                setIsInspecting(false)
            }
        }
        window.addEventListener('hashchange', onHashchange)
        window.addEventListener('resize', onWindowResize)

        return () => {
            window.removeEventListener('resize', onWindowResize)
            window.removeEventListener('hashchange', onHashchange)
        }
    }, [])

    useEffect(() => {
        // Attempt to use fullSizeSrc from props if the fetch fails.
        fetchS3UrlBothQuality(deps, fullSizeSrc).then(({ fullSizeS3Url }) =>
            setFullScreenSrc(fullSizeS3Url || fullSizeSrc)
        )
    }, [fullSizeSrc])

    useEffect(() => {
        if (isInspecting) {
            addImageOpenToURLSearchParams()
        } else if (window.location.hash.includes(FULLSCREEN_QUERY_KEY) && isInitialised) {
            history.back()
        }
        setIsInitialised(true)
    }, [isInspecting])

    useEffect(() => {
        if (!isInspecting) return

        let oldPinchDistance: Nullable<number> = null
        let oldTouchPosition: Nullable<{ x: number; y: number }> = null

        const handleTouchStart = (e: any) => {
            oldTouchPosition = null
            oldPinchDistance = null

            if (e.touches.length === 1) {
                oldTouchPosition = {
                    x: e.touches[0].pageX,
                    y: e.touches[0].pageY,
                }
            }

            if (e.touches.length === 2) {
                const initialDistance = Math.hypot(
                    e.touches[0].pageX - e.touches[1].pageX,
                    e.touches[0].pageY - e.touches[1].pageY
                )
                oldPinchDistance = initialDistance
            }
        }

        const handleTouchMove = (e: TouchEvent) => {
            if (oldPinchDistance != null) {
                const newPinchDistance = Math.hypot(
                    e.touches[0].pageX - e.touches[1].pageX,
                    e.touches[0].pageY - e.touches[1].pageY
                )
                setPositionData(({ zoom, offsetX, offsetY }) => {
                    const newZoomPercent = Math.round(limitNumber(zoom + newPinchDistance - oldPinchDistance, 100, 760))
                    const zoomRatioChange: number = newZoomPercent / zoom
                    const position = getPosition(zoomRatioChange)
                    const { maxX, maxY } = getOffsetLimits(zoomRatioChange)
                    return {
                        ...position,
                        zoom: newZoomPercent,
                        offsetX: limitNumber(zoomRatioChange * offsetX, -maxX, maxX),
                        offsetY: limitNumber(zoomRatioChange * offsetY, -maxY, maxY),
                        maxX,
                        maxY,
                    }
                })
                oldPinchDistance = newPinchDistance
            }

            if (oldTouchPosition != null) {
                // Prevent reload and back navigation
                e.preventDefault()
                const newTouchPosition = {
                    x: e.touches[0].pageX,
                    y: e.touches[0].pageY,
                }
                setPositionData((value) => {
                    const xMovement = newTouchPosition.x - oldTouchPosition.x
                    const yMovement = newTouchPosition.y - oldTouchPosition.y
                    return {
                        ...value,
                        offsetX: limitNumber(value.offsetX + xMovement, -value.maxX, value.maxX),
                        offsetY: limitNumber(value.offsetY + yMovement, -value.maxY, value.maxY),
                    }
                })

                oldTouchPosition = newTouchPosition
            }
        }

        const handleMouseDown = (event: MouseEvent) => {
            event.preventDefault()
            oldPinchDistance = null
            oldTouchPosition = {
                x: event.clientX,
                y: event.clientY,
            }
        }

        const handleMouseMove = (event: MouseEvent) => {
            if (oldTouchPosition) {
                const newTouchPosition = {
                    x: event.clientX,
                    y: event.clientY,
                }
                setPositionData((value) => {
                    const xMovement = newTouchPosition.x - oldTouchPosition.x
                    const yMovement = newTouchPosition.y - oldTouchPosition.y
                    return {
                        ...value,
                        offsetX: limitNumber(value.offsetX + xMovement, -value.maxX, value.maxX),
                        offsetY: limitNumber(value.offsetY + yMovement, -value.maxY, value.maxY),
                    }
                })

                oldTouchPosition = newTouchPosition
            }
        }

        const handleEventEnd = () => {
            oldPinchDistance = null
            oldTouchPosition = null
        }

        const { current } = contentWrapper
        current.addEventListener('mousedown', handleMouseDown)
        current.addEventListener('mousemove', handleMouseMove)
        current.addEventListener('mouseup', handleEventEnd)
        current.addEventListener('mouseleave', handleEventEnd)

        current.addEventListener('touchstart', handleTouchStart)
        current.addEventListener('touchmove', handleTouchMove)
        current.addEventListener('touchend', handleEventEnd)

        return () => {
            current.removeEventListener('mousedown', handleMouseDown)
            current.removeEventListener('mousemove', handleMouseMove)
            current.removeEventListener('mouseup', handleEventEnd)
            current.removeEventListener('mouseleave', handleEventEnd)

            current.removeEventListener('touchstart', handleTouchStart)
            current.removeEventListener('touchmove', handleTouchMove)
            current.removeEventListener('touchend', handleEventEnd)
        }
    }, [isInspecting])

    // This useEffect is a workaround for a bug in iOS safari which affects the iOS cordova build
    // The header and footer and display over the top of the inspecting modal but are not clickable
    // This hides the header and footer while the modal is open.
    // I think it is because of the use of position fixed and I have tried suggested workarounds using
    // I tried suggested fixes online using translate3d but I couldn't get it to work
    // Zaptic bug ticket https://code.zaptic.com/zaptic/zaptic/-/issues/11211
    useEffect(() => {
        const footers = document.getElementsByClassName('AppCore__page__Page__footer')
        const headers = document.getElementsByClassName('AppCore__page__Page__header')
        if (isInspecting) {
            for (const footer of footers) {
                footer.classList.add('InspectableImage__photo__HiddenFooter')
            }

            for (const header of headers) {
                header.classList.add('InspectableImage__photo__HiddenHeader')
            }
        }

        return () => {
            for (const footer of footers) {
                footer.classList.remove('InspectableImage__photo__HiddenFooter')
            }

            for (const header of headers) {
                header.classList.remove('InspectableImage__photo__HiddenHeader')
            }
        }
    }, [isInspecting])

    const handleZoomChange = (proportion: number) => {
        setPositionData(({ zoom, offsetX, offsetY }) => {
            const newZoomPercent = Math.round(Math.max(100, Math.min(760, zoom * proportion)))
            const zoomRatioChange: number = newZoomPercent / zoom
            const position = getPosition(zoomRatioChange)

            const { maxX, maxY } = getOffsetLimits(zoomRatioChange)

            return {
                ...position,
                zoom: newZoomPercent,
                offsetX: limitNumber(zoomRatioChange * offsetX, -maxX, maxX),
                offsetY: limitNumber(zoomRatioChange * offsetY, -maxY, maxY),
                maxX,
                maxY,
            }
        })
    }

    const handleZoomInClick = () => {
        handleZoomChange(1.5)
    }

    const handleZoomOutClick = () => {
        handleZoomChange(2 / 3)
    }

    const handleInspectClick = (event: React.MouseEvent) => {
        if (isInspecting) return

        event.stopPropagation() // Prevents selection and auto-scroll to next element.

        setIsInspecting(true)

        setPositionData({
            zoom: 100,
            left: 0,
            top: 0,
            offsetX: 0,
            offsetY: 0,
            maxX: 0,
            maxY: 0,
        })
    }

    const handlePanRightClick = () => {
        pan({ x: -50, y: 0 })
    }

    const handlePanLeftClick = () => {
        pan({ x: 50, y: 0 })
    }

    const handlePanUpClick = () => {
        pan({ x: 0, y: 50 })
    }

    const handlePanDownClick = () => {
        pan({ x: 0, y: -50 })
    }

    const handleBackButtonClick = (event: React.MouseEvent) => {
        event.stopPropagation() // Prevents auto-scrolling and selecting an image when going back.

        setIsInspecting(false)
    }

    const pan = ({ x, y }: { x: number; y: number }) => {
        setPositionData((value) => {
            if (value.left > 0) return value
            const offsetX = value.offsetX + x
            const offsetY = value.offsetY + y

            return {
                ...value,
                offsetX: limitNumber(offsetX, -value.maxX, value.maxX),
                offsetY: limitNumber(offsetY, -value.maxY, value.maxY),
            }
        })
    }

    return (
        <>
            {isInspecting && (
                <div className="InspectableImage__photo__Inspector">
                    <div className="InspectibleImage__photo__Header">
                        <div className="InspectableImage__photo__HeaderLeft">
                            <zaptic-button type="text" onClick={handleBackButtonClick}>
                                <div className="InspectibleImage__photo__BackButtonContent" slot="icon">
                                    <zaptic-icon
                                        class="InspectibleImage__photo__BackButtonIcon"
                                        icon-name="arrow-right"
                                    />
                                </div>
                            </zaptic-button>
                        </div>
                        <div className="InspectibleImage__photo__HeaderTitle">{title}</div>
                        <div className="InspectableImage__photo__HeaderRight" />
                    </div>
                    <div ref={contentWrapper} className="InspectibleImage__photo__Content">
                        <div
                            className={classNames('InspectibleImage__photo__ZoomIn', {
                                'InspectibleImage__photo__ZoomIn--disabled': positionData.zoom === 760,
                            })}
                            onClick={handleZoomInClick}
                        >
                            <zaptic-icon icon-name="zoom-in" />
                        </div>
                        <div
                            className={classNames('InspectibleImage__photo__ZoomOut', {
                                'InspectibleImage__photo__ZoomOut--disabled': positionData.zoom === 100,
                            })}
                            onClick={handleZoomOutClick}
                        >
                            <zaptic-icon icon-name="zoom-out" />
                        </div>
                        {positionData.maxX !== positionData.offsetX && (
                            <div className="InspectibleImage__photo__PanLeft" onClick={handlePanLeftClick}>
                                <div className="InspectibleImage__photo__PanButton">
                                    <zaptic-icon icon-name="image-arrow" />
                                </div>
                            </div>
                        )}
                        {-positionData.maxX !== positionData.offsetX && (
                            <div className="InspectibleImage__photo__PanRight" onClick={handlePanRightClick}>
                                <div className="InspectibleImage__photo__PanButton">
                                    <zaptic-icon icon-name="image-arrow" />
                                </div>
                            </div>
                        )}
                        {positionData.maxY !== positionData.offsetY && (
                            <div className="InspectibleImage__photo__PanUp" onClick={handlePanUpClick}>
                                <div className="InspectibleImage__photo__PanButton">
                                    <zaptic-icon icon-name="image-arrow" />
                                </div>
                            </div>
                        )}
                        {-positionData.maxY !== positionData.offsetY && (
                            <div className="InspectibleImage__photo__PanDown" onClick={handlePanDownClick}>
                                <div className="InspectibleImage__photo__PanButton">
                                    <zaptic-icon icon-name="image-arrow" />
                                </div>
                            </div>
                        )}
                        <div
                            ref={contentViewBox}
                            className="InspectibleImage__photo__ContentViewBox"
                            style={{
                                width: `${positionData.zoom}%`,
                                height: `${positionData.zoom}%`,
                                top: positionData.top + positionData.offsetY,
                                left: positionData.left + positionData.offsetX,
                            }}
                        >
                            <zaptic-annotated-image
                                class="AppCore__photo__InspectedImage"
                                src={fullScreenSrc}
                                annotation={annotation != null ? annotation : null}
                                data-testid="annotated-image--full"
                            />
                        </div>
                    </div>
                </div>
            )}
            <div
                className={classNames('InspectableImage__photo', {
                    'InspectableImage__photo--clickable': clickableArea == null,
                })}
                onClick={clickableArea == null ? handleInspectClick : null}
            >
                <zaptic-annotated-image
                    class={classNames(imageClass, 'InspectableImage__photo__annotatedPhoto')}
                    src={src}
                    ref={image}
                    annotation={annotation != null ? annotation : null}
                    tile={tile != null ? 'true' : 'false'}
                    data-testid="annotated-image--thumbnail"
                />
                <div
                    className={classNames('InspectableImage__photo__ExpandButton', {
                        'InspectableImage__photo__ExpandButton--topLeft': clickableArea === 'top-left',
                    })}
                    onClick={clickableArea != null ? handleInspectClick : null}
                >
                    <zaptic-icon class="InspectableImage__photo__ExpandButtonIcon" icon-name="expand" />
                </div>
            </div>
        </>
    )
}
function addImageOpenToURLSearchParams() {
    const [base, query] = window.location.href.split('?')
    const searchParams = new URLSearchParams(query ?? '')
    if (!searchParams.has(FULLSCREEN_QUERY_KEY)) {
        searchParams.append(FULLSCREEN_QUERY_KEY, '1')
        history.pushState(null, null, `${base}?${searchParams.toString()}`)
    }
}
